// src/utils/loteSpatialFilter.js
import {
    featureCollection,
    union as turfUnion,
    intersect as turfIntersect,
    buffer as turfBuffer,
    polygonToLine as turfPolygonToLine,
    lineSplit as turfLineSplit,
    lineIntersect as turfLineIntersect,
    multiPoint as turfMultiPoint,
    length as turfLength,
    along as turfAlong,
    point as turfPoint,
    booleanPointInPolygon,
    booleanIntersects
} from '@turf/turf';

const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim());

// Expande la máscara unos metros para no 'cortar' líneas/buffers en caminos/espacios estrechos entre polígonos del mismo filtro.
const MASK_PAD_METERS = 5;

const normalizeKey = (k) =>
    toStr(k)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');

const getAnyProp = (obj, candidateKeys) => {
    if (!obj) return '';
    const map = {};
    Object.keys(obj).forEach((k) => {
        map[normalizeKey(k)] = obj[k];
    });
    for (const k of candidateKeys) {
        const v = map[normalizeKey(k)];
        const s = toStr(v);
        if (s) return s;
    }
    return '';
};

export const hasActiveLoteFilters = (f) =>
    !!toStr(f?.loteId) || !!toStr(f?.finca) || !!toStr(f?.codigoFinca) || !!toStr(f?.lote);

export const matchLoteProps = (propsOrRow, f) => {
    const p = propsOrRow?.properties || propsOrRow || {};
    const eq = (a, b) => String(a ?? '') === String(b ?? '');

    if (toStr(f?.loteId)) {
        const id = toStr(f.loteId);
        const rowId = getAnyProp(p, ['_id','id','ID','LOTE_ID','LOTEID','LoteID','loteId']);
        if (!eq(rowId, id)) return false;
    }

    // Finca (nombre)
    if (toStr(f?.finca)) {
        const name = toStr(f.finca);
        const rowName = getAnyProp(p, [
            // Nombre finca (display)
            'N. FINCA', 'N FINCA', 'N_FINCA', 'NFINCA',
            'Nombre Fin','NOMBRE FIN','NOMBRE_FIN',
            'Nombre Finca','NOMBRE_FINCA',
            'FINCA_NOMBRE','FINCA_NAME'
        ]);
        if (!eq(rowName, name)) return false;
    }

    // Código finca
    if (toStr(f?.codigoFinca)) {
        const cod = toStr(f.codigoFinca);
        const rowCod = getAnyProp(p, [
            // Código finca
            'C. FINCA', 'C FINCA', 'C_FINCA', 'CFINCA',
            'FINCA','finca','CODIGO_FINCA','COD_FINCA','FINCA_CODIGO','CODIGO DE FINCA'
        ]);
        if (!eq(rowCod, cod)) return false;
    }

    if (toStr(f?.lote)) {
        const lote = toStr(f.lote);
        const rowLote = getAnyProp(p, ['LOTE','lote','Lote','NUM_LOTE','LOTE_NUM']);
        if (!eq(rowLote, lote)) return false;
    }

    return true;
};

const buildMask = (clipFC) => {
    const feats = clipFC?.features || [];
    const polys = feats.filter(
        (f) =>
            f?.geometry &&
            (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
    );
    if (!polys.length) return null;

    // union iterativo (robusto con try/catch)
    let mask = polys[0];
    for (let i = 1; i < polys.length; i++) {
        try {
            const u = turfUnion(mask, polys[i]);
            if (u) mask = u;
        } catch {
            // si union falla, seguimos con la máscara actual (mejor que romper)
        }
    }
    // Opcional: expandimos la máscara unos metros para incluir caminos/espacios estrechos entre polígonos del mismo filtro.
    if (MASK_PAD_METERS > 0) {
        try {
            const padded = turfBuffer(mask, MASK_PAD_METERS, { units: 'meters' });
            if (padded) mask = padded;
        } catch {
            // noop
        }
    }
    return mask;
};

const midpointOfLine = (lineFeat) => {
    try {
        const km = turfLength(lineFeat, { units: 'kilometers' });
        if (!Number.isFinite(km) || km <= 0) {
            const c0 = lineFeat?.geometry?.coordinates?.[0];
            return c0 ? turfPoint(c0) : null;
        }
        return turfAlong(lineFeat, km / 2, { units: 'kilometers' });
    } catch {
        const c0 = lineFeat?.geometry?.coordinates?.[0];
        return c0 ? turfPoint(c0) : null;
    }
};

const samplePointsOnLine = (lineFeat) => {
    try {
        const km = turfLength(lineFeat, { units: 'kilometers' });
        if (!Number.isFinite(km) || km <= 0) {
            const c0 = lineFeat?.geometry?.coordinates?.[0];
            const c1 = lineFeat?.geometry?.coordinates?.[lineFeat?.geometry?.coordinates?.length - 1];
            const pts = [];
            if (c0) pts.push(turfPoint(c0));
            if (c1) pts.push(turfPoint(c1));
            return pts;
        }
        const pts = [];
        // 0%, 25%, 50%, 75%, 100%
        const fracs = [0, 0.25, 0.5, 0.75, 1];
        fracs.forEach((f) => {
            try {
                const d = km * f;
                const p = turfAlong(lineFeat, d, { units: 'kilometers' });
                if (p) pts.push(p);
            } catch {
                // noop
            }
        });
        return pts;
    } catch {
        const c0 = lineFeat?.geometry?.coordinates?.[0];
        return c0 ? [turfPoint(c0)] : [];
    }
};

const segmentInsideMask = (seg, mask) => {
    const pts = samplePointsOnLine(seg);
    if (!pts.length) return false;
    let inside = 0;
    for (const p of pts) {
        try {
            if (booleanPointInPolygon(p, mask)) inside += 1;
        } catch {
            // noop
        }
    }
    // Si se ven mayoritariamente dentro, lo consideramos dentro.
    return inside >= Math.ceil(pts.length * 0.6);
};

const clipLineToMask = (lineFeat, mask) => {
    const out = [];

    let splitter;
    try {
        splitter = turfPolygonToLine(mask);
    } catch {
        splitter = null;
    }

    let splitFC = null;

    // 1) Intento principal: split por borde de la máscara
    try {
        splitFC = splitter ? turfLineSplit(lineFeat, splitter) : featureCollection([lineFeat]);
    } catch {
        splitFC = null;
    }

    // 2) Fallback robusto: si el split falla (o no corta) pero la línea cruza el borde,
    // usamos puntos de intersección para dividirla. Esto evita el caso donde se conserva
    // la línea completa por un midpoint dentro (dejando grandes tramos fuera).
    try {
        const needsFallback =
            !splitFC ||
            !Array.isArray(splitFC.features) ||
            splitFC.features.length <= 1;

        if (needsFallback && splitter) {
            const inter = turfLineIntersect(lineFeat, splitter);
            const pts = (inter?.features || [])
                .map((f) => f?.geometry?.coordinates)
                .filter((c) => Array.isArray(c) && c.length === 2);

            if (pts.length > 0) {
                const mp = turfMultiPoint(pts);
                splitFC = turfLineSplit(lineFeat, mp);
            }
        }
    } catch {
        // noop
    }

    // 3) Último fallback: no split -> tratamos como 1 segmento
    if (!splitFC || !Array.isArray(splitFC.features) || splitFC.features.length === 0) {
        splitFC = featureCollection([lineFeat]);
    }

    const refine = (seg, depth = 0) => {
        if (!seg?.geometry) return;

        // Caso normal: tomamos la decisión por muestreo (más robusto que solo midpoint)
        const inside = segmentInsideMask(seg, mask);
        if (inside) {
            // Si parece dentro, pero el split original no cortó bien (casos raros),
            // intentamos un split adicional solo cuando detectamos intersecciones.
            if (depth < 2 && splitter) {
                try {
                    const inter = turfLineIntersect(seg, splitter);
                    const pts = (inter?.features || [])
                        .map((f) => f?.geometry?.coordinates)
                        .filter((c) => Array.isArray(c) && c.length === 2);

                    if (pts.length > 0) {
                        const mp = turfMultiPoint(pts);
                        const reSplit = turfLineSplit(seg, mp);
                        const parts = reSplit?.features || [];

                        // Si realmente se partió, filtramos partes; si no, devolvemos seg.
                        if (parts.length > 1) {
                            parts.forEach((p) => refine(p, depth + 1));
                            return;
                        }
                    }
                } catch {
                    // noop
                }
            }

            out.push(seg);
            return;
        }

        // Si mayoritariamente fuera, pero cruza, intentamos dividir y quedarnos con lo de dentro.
        if (depth < 2 && splitter) {
            try {
                const inter = turfLineIntersect(seg, splitter);
                const pts = (inter?.features || [])
                    .map((f) => f?.geometry?.coordinates)
                    .filter((c) => Array.isArray(c) && c.length === 2);
                if (pts.length > 0) {
                    const mp = turfMultiPoint(pts);
                    const reSplit = turfLineSplit(seg, mp);
                    const parts = reSplit?.features || [];
                    if (parts.length > 1) {
                        parts.forEach((p) => refine(p, depth + 1));
                        return;
                    }
                }
            } catch {
                // noop
            }
        }
    };

    for (const seg of splitFC.features || []) {
        refine(seg, 0);
    }

    // preservar props/id
    return out.map((seg, i) => ({
        ...seg,
        id: lineFeat.id ? `${lineFeat.id}-${i}` : seg.id,
        properties: { ...(lineFeat.properties || {}), ...(seg.properties || {}) },
    }));
};
export function clipFeaturesToPolygons(featuresInput, polygonsInput) {
    const toFC = (input) => {
        if (!input) return { type: 'FeatureCollection', features: [] };
        if (input.type === 'FeatureCollection' && Array.isArray(input.features)) return input;
        if (input.type === 'Feature') return { type: 'FeatureCollection', features: [input] };
        if (Array.isArray(input)) return { type: 'FeatureCollection', features: input.filter(Boolean) };
        return { type: 'FeatureCollection', features: [] };
    };

    const explodeMaskPolys = (maskFC) => {
        const out = [];
        for (const f of (maskFC?.features || [])) {
            const g = f?.geometry;
            if (!g) continue;

            if (g.type === 'Polygon') {
                out.push(f);
            } else if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
                g.coordinates.forEach((coords, idx) => {
                    out.push({
                        type: 'Feature',
                        id: `${f.id ?? 'mask'}-mp-${idx}`,
                        properties: f.properties || {},
                        geometry: { type: 'Polygon', coordinates: coords },
                    });
                });
            }
        }
        return out;
    };

    // Fallback (solo cuando NO usamos máscara unida):
    // recorta una línea contra UN polígono (puede provocar “cortes internos” si lo aplicas por cada polígono)
    const clipLineToPolygon = (lineFeat, polyFeat) => {
        try {
            if (!lineFeat?.geometry || !polyFeat?.geometry) return [];

            // Fast reject
            if (!booleanIntersects(lineFeat, polyFeat)) return [];

            const boundary = turfPolygonToLine(polyFeat);
            const splitFC = turfLineSplit(lineFeat, boundary);

            const segments =
                (splitFC?.features && splitFC.features.length) ? splitFC.features : [lineFeat];

            const kept = [];
            for (let i = 0; i < segments.length; i++) {
                const seg = segments[i];
                if (!seg?.geometry) continue;

                // midpoint
                const km = turfLength(seg, { units: 'kilometers' });
                const mid =
                    km > 0
                        ? turfAlong(seg, km / 2, { units: 'kilometers' })
                        : turfPoint(seg.geometry.coordinates?.[0]);

                if (mid && booleanPointInPolygon(mid, polyFeat)) {
                    kept.push({
                        type: 'Feature',
                        geometry: seg.geometry,
                        properties: { ...(lineFeat.properties || {}) },
                        id: `${lineFeat.id ?? 'line'}-seg-${i}`,
                    });
                }
            }
            return kept;
        } catch (e) {
            // Fallback conservador: si intersecta, devolvemos la línea original
            try {
                if (booleanIntersects(lineFeat, polyFeat)) {
                    return [{
                        type: 'Feature',
                        geometry: lineFeat.geometry,
                        properties: { ...(lineFeat.properties || {}) },
                        id: `${lineFeat.id ?? 'line'}-fallback`,
                    }];
                }
            } catch {}
            return [];
        }
    };

    const maskFC = toFC(polygonsInput);
    const maskPolys = explodeMaskPolys(maskFC);
    if (!maskPolys.length) return toFC(featuresInput);

    // ✅ Máscara unida (union) para evitar cortes internos entre polígonos seleccionados
    const mask = buildMask(maskFC);

    const inputFC = toFC(featuresInput);
    const out = [];

    const pushWithProps = (baseFeature, clippedFeature) => {
        out.push({
            type: 'Feature',
            id: clippedFeature.id ?? baseFeature.id,
            geometry: clippedFeature.geometry,
            properties: { ...(baseFeature.properties || {}) },
        });
    };

    for (const feat of (inputFC.features || [])) {
        const g = feat?.geometry;
        if (!g) continue;

        // Puntos
        if (g.type === 'Point') {
            const inside = mask
                ? booleanPointInPolygon(feat, mask)
                : maskPolys.some((p) => booleanPointInPolygon(feat, p));

            if (inside) out.push(feat);
            continue;
        }

        // Líneas
        if (g.type === 'LineString') {
            // ✅ Union: recorta contra el borde exterior, sin cortes internos
            const pieces = mask
                ? clipLineToMask(feat, mask)
                : (() => {
                    const tmp = [];
                    maskPolys.forEach((p) => tmp.push(...clipLineToPolygon(feat, p)));
                    return tmp;
                })();

            pieces.forEach((p) => pushWithProps(feat, p));
            continue;
        }

        if (g.type === 'MultiLineString') {
            // Explota a LineString y recorta cada una
            const coords = g.coordinates || [];
            coords.forEach((lsCoords, idx) => {
                const asLine = {
                    type: 'Feature',
                    id: `${feat.id ?? 'ml'}-${idx}`,
                    properties: feat.properties || {},
                    geometry: { type: 'LineString', coordinates: lsCoords },
                };

                // ✅ Union: recorta contra el borde exterior, sin cortes internos
                const pieces = mask
                    ? clipLineToMask(asLine, mask)
                    : (() => {
                        const tmp = [];
                        maskPolys.forEach((p) => tmp.push(...clipLineToPolygon(asLine, p)));
                        return tmp;
                    })();

                pieces.forEach((p) => pushWithProps(asLine, p));
            });
            continue;
        }

        // Polígonos (incluye buffers, overlaps, etc.)
        if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
            // ✅ Con union: una sola intersección (evita “pedacear” por límites internos)
            if (mask) {
                try {
                    const inter = turfIntersect(feat, mask);
                    if (inter?.geometry) pushWithProps(feat, inter);
                } catch {
                    // fallback al comportamiento anterior
                    for (const mp of maskPolys) {
                        const inter = turfIntersect(feat, mp);
                        if (inter?.geometry) pushWithProps(feat, inter);
                    }
                }
            } else {
                for (const mp of maskPolys) {
                    const inter = turfIntersect(feat, mp);
                    if (inter?.geometry) pushWithProps(feat, inter);
                }
            }
            continue;
        }

        // Otros => por defecto, no tocar
        out.push(feat);
    }

    return featureCollection(out);
}
